import { PaginatedResponse } from './common'
import { SessionStatus } from './session'

// Session summary for adventure detail view
export interface AdventureSessionDto {
  id: string
  startedAt: string
  status: SessionStatus
  completedAt: string | null
  lastActivityAt: string | null
  isTestSession: boolean
  duration: string // Format: "HH:mm:ss.fffffff"
}

export interface AdventureDto {
  id: string
  title: string
  description: string
  fullDescription: string
  quickDescription?: string
  difficulty: number // 1-5
  difficultyLevel?: number // Alias for difficulty
  status: number // AdventureStatus enum: 1=Draft, 2=Testing, 3=Published, 4=ModeratorReview, 5=Archived, 6=Suspended
  createdBy: string
  createdByName: string
  /** Backend alias for the creator's user id (mirrors AdventureDto.CreatorId). */
  creatorId?: string
  createdDate: string
  /** Date the current user completed this adventure (only populated on personalized list responses). */
  completedDate?: string | null
  // Owner entity (replaces createdBy/createdByName for display)
  owner?: {
    entityId: string
    entityTypeId: number
    entityTypeName: string
    entityName: string
    entityAvatarUrl?: string
  }
  modifiedDate: string
  imageUrl?: string
  thumbnailUrl?: string
  coverUrl?: string
  thumbnailImageUrl?: string
  coverImageUrl?: string
  thumbnailImageId?: string
  coverImageId?: string
  tags: string[]
  participantCount: number
  completionCount: number
  averageRating?: number
  // Detailed rating breakdown (from adventure detail API)
  rating?: {
    averagePresenceScore: number
    averageObscuraScore: number
    averageTrailcraftScore: number
    averageCraftmarkScore?: number | null
    averageDifficultyScore?: number | null
    overallAverageScore: number
    totalRatings: number
    difficultyRatingCount?: number
    targetType: number
    targetId: string
    ratingDistribution?: Record<string, number>
  }
  isPublic: boolean
  mainCategory?: number // Adventure main category enum (1-6)
  // Editor-specific fields
  startDate?: string
  endDate?: string
  latitude?: number
  longitude?: number
  allowGroups?: boolean
  // Group play status from API: 0=Not supported, 1=Groups allowed, 2=Free tier (not available)
  groupPlayStatus?: number
  maxParticipants?: number
  isRepeatable?: boolean
  // Session-related fields (from adventure detail API)
  isStarted?: boolean
  isCompleted?: boolean
  sessions?: AdventureSessionDto[]
  totalSessionCount?: number
  location?: {
    latitude: number
    longitude: number
    address?: string
  }
  // Silent adventure fields
  isSilentJoin?: boolean
  isSilentComplete?: boolean
  silentCompleteDurationMinutes?: number
  // Group participation settings
  groupParticipationSettings?: {
    votingVisibility: number
    voteChangePolicy: number
    voteChangeWindowSeconds?: number | null
    votingCompletionMode: number
    votingCompletionPercentage?: number | null
    votingCompletionTimerSeconds?: number | null
    votingTieResolution: number
    votingTieTimeoutSeconds?: number | null
    offlineMemberAction: number
    maxParticipantDistanceFeet?: number | null
    participantProximityPercentage?: number | null
  }
  // Eligibility fields (from search results)
  isEligible?: boolean
  meetsDisplayCriteria?: boolean
  meetsJoinCriteria?: boolean
  joinCriteriaFailureReasons?: string[]
  requiresSubscription?: boolean
  approxLatitude?: number
  approxLongitude?: number
  // Player activity stats (from search results)
  uniquePlayersLastHour?: number
  uniquePlayersLast24Hours?: number
  uniquePlayersLastMonth?: number
  uniquePlayersLastYear?: number
  // User's relationship to this adventure (bitmask)
  // Joined=1, Maintainer=2, Tester=4, Moderator=8
  userRelationType?: number
  /** True when the current user can manage this adventure (creator, maintainer, or moderator). */
  canManage?: boolean
  /** Optional adventure-type tag IDs surfaced by personalized endpoints; absent on plain detail/search responses. */
  tagsAdventureTypes?: string[] | null
  // Test mode flag - true when adventure has a Testing workflow version
  // and current user is creator, maintainer, or tester
  hasTestVersion?: boolean
  // Workflow version numbers (only populated on detail responses, not search)
  testingWorkflowVersion?: number | null
  publishedWorkflowVersion?: number | null
  latestWorkflowVersion?: number
  // Moderation features
  hasModerationNodes?: boolean
  // Available group sessions count (for badge display)
  availableGroupSessionCount?: number
  // Geocache-specific fields
  geocacheCode?: string
  geocacheCodeUrl?: string
}

export interface AdventureListParams {
  page?: number
  pageSize?: number
  search?: string
  difficulty?: number
  status?: string
  tags?: string[]
  sortBy?: 'title' | 'createdDate' | 'difficulty' | 'popularity'
  sortOrder?: 'asc' | 'desc'
}

export interface CreateAdventureRequest {
  title: string
  description: string
  quickDescription?: string
  location: string
  startDate?: string | null
  endDate?: string | null
  latitude?: number | null
  longitude?: number | null
  categoryId: number // Adventure category ID
  isRepeatable: boolean
  isPublic: boolean
  allowGroups: boolean
  maxParticipants: number
  difficultyLevel: number
  useDisplayForJoining: boolean
  // Silent adventure fields
  isSilentJoin?: boolean
  isSilentComplete?: boolean
  // Clue/hint settings
  maxCluesAllowed?: number
  allowRepeatAfterClueFailure?: boolean
  showOnScoreboardAfterClueRepeat?: boolean
  // Statistics mode
  statsMode?: number
  // Owner group (if creating on behalf of a group)
  ownerGroupId?: string | null
}

export interface UpdateAdventureRequest {
  id: string
  title: string
  description: string
  quickDescription?: string
  fullDescription?: string
  location: string
  startDate?: string | null
  endDate?: string | null
  latitude?: number | null
  longitude?: number | null
  categoryId: number // Adventure category ID
  isRepeatable: boolean
  isPublic: boolean
  allowGroups: boolean
  maxParticipants: number
  difficultyLevel: number
  useDisplayForJoining: boolean
  status?: 'Draft' | 'Published' | 'Archived'
  // Silent adventure fields
  isSilentJoin?: boolean
  isSilentComplete?: boolean
  // Clue/hint settings
  maxCluesAllowed?: number
  allowRepeatAfterClueFailure?: boolean
  showOnScoreboardAfterClueRepeat?: boolean
  // Statistics mode
  statsMode?: number
  // Image fields
  thumbnailImageId?: string | null
  coverImageId?: string | null
  thumbnailImageUrl?: string | null
  coverImageUrl?: string | null
}

export type AdventureListResponse = PaginatedResponse<AdventureDto>

// Saved Filter Types
export interface AdventureSearchRequest {
  userLatitude?: number | null
  userLongitude?: number | null
  viewportNorthEastLat?: number | null
  viewportNorthEastLng?: number | null
  viewportSouthWestLat?: number | null
  viewportSouthWestLng?: number | null
  useViewportBounds?: boolean
  radiusFeet?: number | null
  radiusMiles?: number | null
  searchTerm?: string | null
  status?: number | null // AdventureStatus enum
  mainCategory?: number[] | null // AdventureMainCategory enum (supports multiple)
  tags?: string[] | null
  tagsInterest?: string[] | null
  tagsGender?: string[] | null
  tagsAdventure?: string[] | null
  tagsAdventureTypes?: string[] | null
  minDifficulty?: number | null // 1-5
  maxDifficulty?: number | null // 1-5
  minDifficultyLevel?: number | null // 1-5
  maxDifficultyLevel?: number | null // 1-5
  difficultyLevel?: number | null // 1-5
  minRating?: number | null // 0-5
  minPoints?: number | null // 0-10000
  maxPoints?: number | null // 0-10000
  pageSize?: number
  page?: number
  sortBy?: string | null
  sortDirection?: string | null
  onlyEligible?: boolean
  enforceDisplayCriteria?: boolean
  allowGroups?: boolean | null
  isRepeatable?: boolean | null
  hasAvailableSpots?: boolean | null
  startDateFrom?: string | null
  startDateTo?: string | null
  savedFilterId?: string | null
  sessionId?: string | null
  myAdventuresOnly?: boolean // Filter to only show adventures where user is owner, maintainer, or moderator
}

export interface SavedSearchFilterDto {
  id: string
  name: string
  description?: string | null
  filterCriteria: AdventureSearchRequest
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateSavedSearchFilterRequest {
  name: string
  description?: string | null
  filterCriteria: AdventureSearchRequest
  isDefault?: boolean
}

// Statistic types for adventure categories
export enum StatisticTypes {
  None = 0,
  Leaderboard = 1,
  CompletionList = 2,
}

export interface AdventureCategoryDto {
  id: number
  name: string
  displayName: string
  description: string
  iconName: string // Emoji icon
  color: string
  features: string[] | null
  workflowAllowed: boolean
  workflowRequired: boolean
  isPublicAllowed: boolean | null
  groupsAllowed: boolean | null
  repeatableAllowed: boolean | null
  allowedNodes: number[] | null
  allowedJoinCriteria: number[] | null
  allowedDisplayCriteria: number[] | null
  allowedLogicCriteria: number[] | null
  statisticTypes: StatisticTypes
  specialRatingCategories: string[] | null
  requiredUserFlags: number
  isActive: boolean
  sortOrder: number
  adventureCount: number
  createdDate: string
  updatedDate: string
}

export interface CriteriaTypeDto {
  id: number
  name: string
  description: string
  category: string
}

export interface NodeTypeDto {
  id: number
  name: string
  displayName: string
  description: string
  category: string
}

// Adventure Join Types
export enum AdventureJoinType {
  Solo = 0,
  Group = 1,
  Public = 2
}

export interface AdventureJoinRequest {
  adventureId: string
  joinType: AdventureJoinType
  startDateTime?: string
  groupName?: string
  groupDescription?: string
  maxMembers?: number
  genderTags?: string[]
  interestTags?: string[]
  lookingForTags?: string[]
  // Group play fields
  masterGroupId?: string
  sessionName?: string
  sessionDescription?: string
  maxParticipants?: number
  requireTagMatch?: boolean
}

export interface AdventureJoinResponse {
  success: boolean
  message?: string
  groupId?: string
  sessionId?: string
  queueId?: string
  startDateTime?: string
  errors?: string[]
}
